import { useNavigate } from "react-router-dom";
import { Row, ContentCard, Button, ResponsiveFlex } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import ReactMarkdown from "react-markdown";
import { dollars, modules } from "byzantine";
import filters from "byzantine/src/filters";
import { useRecipient } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";
import {
  useIsBusinessUser,
  useIsInternationalWireDualApprovalRequired,
} from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import {
  createDefaultValues,
  useCreateInternationalWireForm,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { wireTypeSelectorForm } from "byzantine/src/dbbl/businessLogic/modules/internationalWires/WireTransferContainer.form";
import { PAYMENT_ACTIVITY_ROUTE } from "../../../PaymentActivity/routes";
import { getDisplayedFrequency } from "../../FrequencySelector";
import styles from "./InternationalWire.module.scss";

const ReceiptScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { getValues, reset } = useCreateInternationalWireForm();
  const { resetForm: resetWireTypeSelector } = wireTypeSelectorForm.useForm();
  const { resetForm: resetMethodSelector } =
    modules.wireTemplates.methodSelectorForm.useForm();
  const { resetForm: resetSendViaTemplateForm } =
    modules.wireTemplates.sendViaTemplateForm.useForm();
  const {
    amount,
    recipientId,
    currencyTransferPreference,
    quote,
    id,
    hold_until: holdUntil,
    recurringRule,
    scheduledWire,
  } = getValues();
  const isReceivePreference = currencyTransferPreference === "receive";
  const frequency = getDisplayedFrequency(
    recurringRule?.frequency || "",
    l10n
  );
  const selectedRecipient = useRecipient(recipientId);
  const institution = useInstitution();
  const internationalWireDualApprovalRequired =
    useIsInternationalWireDualApprovalRequired();
  const isBusinessUser = useIsBusinessUser();

  const internationalWireFees = institution?.international_wire_fee || [];
  const fee =
    internationalWireFees?.[0]?.amount !== undefined
      ? `$${internationalWireFees[0].amount}`
      : "";

  const cancelableUntil = scheduledWire?.cancelable_until || holdUntil;
  const cancelableUntilFormatted = cancelableUntil
    ? `${filters.time(cancelableUntil)} on ${filters.shortMonthDayYear(
        cancelableUntil
      )}`
    : null;

  const contactInfo = institution?.help_email
    ? `[${institution.long_name}](mailto:${institution.help_email})`
    : institution?.long_name || "";

  if (!selectedRecipient) return null;

  const { name } = selectedRecipient;

  const handleClickDone = () => {
    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${id}`);
  };

  const handleClickCTA = () => {
    reset(createDefaultValues());
    resetWireTypeSelector();
    resetMethodSelector();
    resetSendViaTemplateForm();
    navigate("/transfer/wires");
  };

  return (
    <div className={styles.receiptContainer}>
      <ContentCard>
        <div className={styles.cardContainer}>
          <h3 className="fontColor--heading">
            {internationalWireDualApprovalRequired
              ? l10n.getString("international-wire-submitted-for-approval")
              : l10n.getString("international-wire-submitted")}
            <i className="narmi-icon-check margin--left--xs" />
          </h3>
          <div className={styles.contentContainer}>
            <ResponsiveFlex gapSize="l">
              <Row>
                <Row.Item>
                  <p>{l10n.getString("label-to")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{name}</p>
                </Row.Item>
              </Row>
              <Row>
                <Row.Item>
                  <p>
                    {isReceivePreference && quote
                      ? l10n.getString("label-estimated-amount")
                      : l10n.getString("label-amount")}
                  </p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{`${filters.currency(
                    dollars(
                      isReceivePreference && quote
                        ? quote.converted_amount || "0"
                        : amount
                    ),
                    {
                      hasDecimal: true,
                    }
                  )}`}</p>
                </Row.Item>
              </Row>
              {fee && (
                <Row>
                  <Row.Item>
                    <p>{l10n.getString("label-fees")}</p>
                  </Row.Item>
                  <Row.Item shrink>
                    <p>{fee}</p>
                  </Row.Item>
                </Row>
              )}
              {recurringRule?.isVisible && frequency && (
                <Row>
                  <Row.Item>
                    <p>{l10n.getString("label-frequency")}</p>
                  </Row.Item>
                  <Row.Item shrink>
                    <p>{frequency}</p>
                  </Row.Item>
                </Row>
              )}
              <Row>
                <Row.Item>
                  <p>{l10n.getString("label-delivery-speed")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{l10n.getString("international-wire-receipt-delivery")}</p>
                </Row.Item>
              </Row>
            </ResponsiveFlex>
          </div>
          {!isBusinessUser && cancelableUntilFormatted && (
            <div
              className={styles.holdUntilDisclosure}
              style={{ marginTop: "-20px", marginBottom: "40px" }}
            >
              <ReactMarkdown>
                {l10n.getString("label-international-wire-review-hold-until", {
                  holdUntilTime: cancelableUntilFormatted,
                  contactInfo,
                })}
              </ReactMarkdown>
              <p>
                {l10n.getString("label-international-wire-suspect-an-error")}
              </p>
            </div>
          )}
          <div className={styles.buttonContainer}>
            <Button
              label={l10n.getString("label-done")}
              testId="international-wire-receipt-done"
              onClick={handleClickDone}
            />
            <Button
              kind="secondary"
              label={l10n.getString("international-wire-receipt-cta")}
              testId="international-wire-receipt-cta-button"
              onClick={handleClickCTA}
            />
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default ReceiptScreen;
